import ExcelJS from 'exceljs';
import type {
  CreateCareerBulkItemResult,
} from '../../../shared/dto/EntityRepository';
import type {
  CreateCareerBulkItemReq,
  CreateCareerImportReq,
} from '../../../shared/dto/ReqDtoRepository';
import { CareerPeriodType, CareerType } from '../../../shared/enum/EnumRepository';
import { isValidHttpUrl } from '../../../shared/util/urlValidation';
import {
  CAREER_BULK_TEMPLATE_HEADERS,
  CAREER_BULK_TEMPLATE_SHEET_NAME,
} from './buildCareerBulkTemplate';
import { CAREER_TYPE_VALUES, getCareerPeriodType } from './careerBulkConfig';
import { stripIncompatibleXlsxComments } from './stripIncompatibleXlsxComments';

type CareerColumn = (typeof CAREER_BULK_TEMPLATE_HEADERS)[number];

const HEADER_MAP: Record<string, CareerColumn> = {
  careertype: 'careerType',
  경력유형: 'careerType',
  유형: 'careerType',
  content: 'content',
  내용: 'content',
  contenten: 'contentEn',
  영문내용: 'contentEn',
  startyear: 'startYear',
  시작연도: 'startYear',
  startmonth: 'startMonth',
  시작월: 'startMonth',
  startday: 'startDay',
  시작일: 'startDay',
  endyear: 'endYear',
  종료연도: 'endYear',
  endmonth: 'endMonth',
  종료월: 'endMonth',
  endday: 'endDay',
  종료일: 'endDay',
  iscurrent: 'isCurrent',
  현재진행: 'isCurrent',
  currentlabelorig: 'currentLabelOrig',
  현재표시: 'currentLabelOrig',
  relatedurl: 'relatedUrl',
  관련url: 'relatedUrl',
  url: 'relatedUrl',
};

const REQUIRED_HEADERS: CareerColumn[] = ['careerType', 'content'];
const URL_FIELDS = new Set<CareerColumn>(['relatedUrl']);

export type CareerBulkSheetParseErrorCode =
  | 'WORKBOOK_LOAD_FAILED'
  | 'NO_SHEET'
  | 'UNKNOWN_HEADER'
  | 'BLANK_HEADER_WITH_DATA'
  | 'DUPLICATE_HEADER'
  | 'MISSING_REQUIRED_HEADER';

export class CareerBulkSheetParseError extends Error {
  constructor(
    public readonly code: CareerBulkSheetParseErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'CareerBulkSheetParseError';
  }
}

export interface CareerBulkSheetParseResult {
  careerList: CreateCareerBulkItemReq[];
  invalidResults: CreateCareerBulkItemResult[];
  totalCount: number;
}

const normalizeHeader = (raw: string): string =>
  raw.trim().toLowerCase().replace(/[\s_-]+/g, '');

const cellToString = (
  value: ExcelJS.CellValue,
  preferHyperlink = false,
): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object') {
    if ('hyperlink' in value) {
      const link = value as { text?: string; hyperlink?: string };
      const selected = preferHyperlink
        ? link.hyperlink || link.text
        : link.text || link.hyperlink;
      return (selected ?? '').trim();
    }
    if ('result' in value) {
      return cellToString(value.result as ExcelJS.CellValue, preferHyperlink);
    }
    if ('text' in value && typeof value.text === 'string') {
      return value.text.trim();
    }
    if ('richText' in value && Array.isArray(value.richText)) {
      return value.richText
        .map((part: { text?: string }) => part.text ?? '')
        .join('')
        .trim();
    }
  }
  return '';
};

const hasCellData = (value: ExcelJS.CellValue): boolean => {
  if (value == null) return false;
  if (typeof value === 'object'
    && ('formula' in value || 'sharedFormula' in value || 'error' in value)) {
    return true;
  }
  return cellToString(value, true) !== '';
};

const getCellError = (value: ExcelJS.CellValue): string | null => {
  if (value == null || value instanceof Date || typeof value !== 'object') {
    return null;
  }
  if ('error' in value) return String(value.error);
  if ('result' in value) {
    if (value.result == null) return 'formula has no cached result';
    return getCellError(value.result as ExcelJS.CellValue);
  }
  if ('formula' in value || 'sharedFormula' in value) {
    return 'formula has no cached result';
  }
  return null;
};

const getColumnKeys = (
  sheet: ExcelJS.Worksheet,
): { columnKeys: Array<CareerColumn | null>; colCount: number } => {
  const headerRow = sheet.getRow(1);
  const colCount = Math.max(sheet.columnCount, headerRow.cellCount);
  const columnKeys: Array<CareerColumn | null> = [];
  const mappedHeaders = new Set<CareerColumn>();

  for (let col = 1; col <= colCount; col += 1) {
    const rawHeader = cellToString(headerRow.getCell(col).value);
    if (!rawHeader) {
      let hasData = false;
      sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber > 1 && hasCellData(row.getCell(col).value)) hasData = true;
      });
      if (hasData) {
        throw new CareerBulkSheetParseError(
          'BLANK_HEADER_WITH_DATA',
          `${sheet.name} column ${col} contains data but has no header.`,
        );
      }
      columnKeys[col] = null;
      continue;
    }

    const mapped = HEADER_MAP[normalizeHeader(rawHeader)];
    if (!mapped) {
      throw new CareerBulkSheetParseError(
        'UNKNOWN_HEADER',
        `Unknown header "${rawHeader}" in ${sheet.name} column ${col}.`,
      );
    }
    if (mappedHeaders.has(mapped)) {
      throw new CareerBulkSheetParseError(
        'DUPLICATE_HEADER',
        `Header "${rawHeader}" duplicates ${mapped} in ${sheet.name}.`,
      );
    }
    mappedHeaders.add(mapped);
    columnKeys[col] = mapped;
  }

  const missingHeaders = REQUIRED_HEADERS.filter((key) => !mappedHeaders.has(key));
  if (missingHeaders.length > 0) {
    throw new CareerBulkSheetParseError(
      'MISSING_REQUIRED_HEADER',
      `Missing required headers in ${sheet.name}: ${missingHeaders.join(', ')}.`,
    );
  }
  return { columnKeys, colCount };
};

const parseCareerType = (raw: string): CareerType | undefined => {
  const token = raw.trim().toUpperCase().replace(/[\s-]+/g, '_');
  return CAREER_TYPE_VALUES.find((type) => type === token);
};

const parseBoolean = (raw: string | undefined): boolean | undefined => {
  if (raw == null || !raw.trim()) return undefined;
  const token = raw.trim().toUpperCase();
  if (['TRUE', 'YES', 'Y', '1', '공개', '현재'].includes(token)) return true;
  if (['FALSE', 'NO', 'N', '0', '비공개', '종료'].includes(token)) return false;
  return undefined;
};

const parseDatePart = (
  value: string | undefined,
  field: CareerColumn,
  min: number,
  max: number,
  errors: string[],
): string | undefined => {
  if (value == null || !value.trim()) return undefined;
  if (!/^\d+$/.test(value.trim())) {
    errors.push(`${field} must be an integer.`);
    return undefined;
  }
  const number = Number(value);
  if (!Number.isSafeInteger(number) || number < min || number > max) {
    errors.push(`${field} must be between ${min} and ${max}.`);
    return undefined;
  }
  return String(number);
};

const validateDateParts = (
  prefix: 'start' | 'end',
  year: string | undefined,
  month: string | undefined,
  day: string | undefined,
  errors: string[],
): void => {
  if (!year && (month || day)) {
    errors.push(`${prefix}Year is required when month or day is set.`);
    return;
  }
  if (!month && day) {
    errors.push(`${prefix}Month is required when day is set.`);
    return;
  }
  if (!year || !month || !day) return;
  const daysInMonth = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
  if (Number(day) > daysInMonth) {
    errors.push(`${prefix}Day is not valid for the given year and month.`);
  }
};

const isAfter = (
  startYear?: string,
  startMonth?: string,
  startDay?: string,
  endYear?: string,
  endMonth?: string,
  endDay?: string,
): boolean => {
  if (!startYear || !endYear) return false;
  if (startYear !== endYear) return Number(startYear) > Number(endYear);
  if (!startMonth || !endMonth) return false;
  if (startMonth !== endMonth) return Number(startMonth) > Number(endMonth);
  return !!startDay && !!endDay && Number(startDay) > Number(endDay);
};

const invalidResult = (
  rowNumber: number,
  errors: string[],
): CreateCareerBulkItemResult => ({
  rowNumber,
  status: 'INVALID',
  careerId: null,
  message: errors.join(' '),
});

const loadWorkbook = async (data: ArrayBuffer): Promise<ExcelJS.Workbook> => {
  const workbook = new ExcelJS.Workbook();
  try {
    await workbook.xlsx.load(data);
    return workbook;
  } catch {
    try {
      const sanitizedData = await stripIncompatibleXlsxComments(data);
      if (!sanitizedData) throw new Error('No incompatible comments found.');
      const sanitizedWorkbook = new ExcelJS.Workbook();
      await sanitizedWorkbook.xlsx.load(sanitizedData);
      return sanitizedWorkbook;
    } catch {
      throw new CareerBulkSheetParseError(
        'WORKBOOK_LOAD_FAILED',
        '엑셀 파일을 읽을 수 없습니다. 올바른 .xlsx 파일인지 확인해 주세요.',
      );
    }
  }
};

export const parseCareerBulkSheet = async (
  file: File,
): Promise<CareerBulkSheetParseResult> => {
  let data: ArrayBuffer;
  try {
    data = await file.arrayBuffer();
  } catch {
    throw new CareerBulkSheetParseError(
      'WORKBOOK_LOAD_FAILED',
      '엑셀 파일을 읽을 수 없습니다. 올바른 .xlsx 파일인지 확인해 주세요.',
    );
  }
  const workbook = await loadWorkbook(data);
  const normalizedSheetName = normalizeHeader(CAREER_BULK_TEMPLATE_SHEET_NAME);
  const sheet = workbook.worksheets.find(
    (candidate) => normalizeHeader(candidate.name) === normalizedSheetName,
  ) ?? workbook.worksheets[0];
  if (!sheet) {
    throw new CareerBulkSheetParseError('NO_SHEET', 'The workbook has no sheet.');
  }

  const { columnKeys, colCount } = getColumnKeys(sheet);
  const careerList: CreateCareerBulkItemReq[] = [];
  const invalidResults: CreateCareerBulkItemResult[] = [];
  let totalCount = 0;

  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;
    const values: Partial<Record<CareerColumn, string>> = {};
    const errors: string[] = [];
    let hasData = false;

    for (let col = 1; col <= colCount; col += 1) {
      const key = columnKeys[col];
      if (!key) continue;
      const cellValue = row.getCell(col).value;
      if (hasCellData(cellValue)) hasData = true;
      const text = cellToString(cellValue, URL_FIELDS.has(key));
      if (text) values[key] = text;
      const cellError = getCellError(cellValue);
      if (cellError) errors.push(`${key}: ${cellError}.`);
    }

    if (!hasData) return;
    totalCount += 1;
    const missingFields = REQUIRED_HEADERS.filter((key) => !values[key]);
    if (missingFields.length > 0) {
      errors.push(`Missing required fields: ${missingFields.join(', ')}.`);
    }

    const careerType = values.careerType
      ? parseCareerType(values.careerType)
      : undefined;
    if (values.careerType && !careerType) {
      errors.push(`careerType must be one of: ${CAREER_TYPE_VALUES.join(', ')}.`);
    }
    if (values.content && values.content.length > 255) {
      errors.push('content must be 255 characters or fewer.');
    }
    if (values.contentEn && values.contentEn.length > 255) {
      errors.push('contentEn must be 255 characters or fewer.');
    }
    if (values.currentLabelOrig && values.currentLabelOrig.length > 20) {
      errors.push('currentLabelOrig must be 20 characters or fewer.');
    }
    if (values.relatedUrl && values.relatedUrl.length > 500) {
      errors.push('relatedUrl must be 500 characters or fewer.');
    } else if (values.relatedUrl && !isValidHttpUrl(values.relatedUrl)) {
      errors.push('relatedUrl must be a valid http/https URL.');
    }

    const startYear = parseDatePart(values.startYear, 'startYear', 1, 9999, errors);
    const startMonth = parseDatePart(values.startMonth, 'startMonth', 1, 12, errors);
    const startDay = parseDatePart(values.startDay, 'startDay', 1, 31, errors);
    const endYear = parseDatePart(values.endYear, 'endYear', 1, 9999, errors);
    const endMonth = parseDatePart(values.endMonth, 'endMonth', 1, 12, errors);
    const endDay = parseDatePart(values.endDay, 'endDay', 1, 31, errors);
    validateDateParts('start', startYear, startMonth, startDay, errors);
    validateDateParts('end', endYear, endMonth, endDay, errors);

    const parsedCurrent = parseBoolean(values.isCurrent);
    if (values.isCurrent && parsedCurrent == null) {
      errors.push('isCurrent must be TRUE or FALSE.');
    }
    const isCurrent = parsedCurrent ?? false;
    const hasEndDate = !!(endYear || endMonth || endDay);

    if (careerType) {
      const periodType = getCareerPeriodType(careerType);
      if (periodType === CareerPeriodType.POINT && hasEndDate) {
        errors.push('End date is only available for PERIOD career types.');
      }
      if (periodType === CareerPeriodType.POINT && isCurrent) {
        errors.push('isCurrent is only available for PERIOD career types.');
      }
    }
    if (isCurrent && hasEndDate) {
      errors.push('End date must be empty when isCurrent is TRUE.');
    }
    if (!isCurrent && values.currentLabelOrig) {
      errors.push('currentLabelOrig is only available when isCurrent is TRUE.');
    }
    if (isAfter(startYear, startMonth, startDay, endYear, endMonth, endDay)) {
      errors.push('End date must not be earlier than start date.');
    }

    if (errors.length > 0 || !careerType || !values.content) {
      invalidResults.push(invalidResult(rowNumber, errors));
      return;
    }

    const career: CreateCareerImportReq = {
      careerType,
      content: values.content,
      isCurrent,
    };
    if (values.contentEn) career.contentEn = values.contentEn;
    if (startYear) career.startYear = startYear;
    if (startMonth) career.startMonth = startMonth;
    if (startDay) career.startDay = startDay;
    if (endYear) career.endYear = endYear;
    if (endMonth) career.endMonth = endMonth;
    if (endDay) career.endDay = endDay;
    if (values.currentLabelOrig) career.currentLabelOrig = values.currentLabelOrig;
    if (values.relatedUrl) career.relatedUrl = values.relatedUrl;
    careerList.push({ rowNumber, career });
  });

  return { careerList, invalidResults, totalCount };
};
